import { usePermissions } from './usePermissions';
import { useAuth } from '../context/AuthContext';

/**
 * Hook خاص بالتحقق من صلاحيات السندات المتخصصة
 * يستخدم للتحقق من صلاحيات المستخدم للوصول إلى صفحات السندات المتخصصة
 */
export const useSpecializedVoucherPermissions = () => {
  const { hasPermission } = usePermissions();
  const { user } = useAuth();

  /**
   * التحقق من صلاحية الوصول لقالب سند متخصص
   * @param templateId معرف قالب السند المتخصص
   * @param action الإجراء المطلوب التحقق منه
   * @returns {boolean} هل لدى المستخدم صلاحية أم لا
   */
  const hasVoucherTemplatePermission = (templateId: string, action: string): boolean => {
    // إذا كان المستخدم مسؤولاً (admin)، أعط صلاحية كاملة
    if (user?.role === 'admin') {
      return true;
    }

    // التحقق من صلاحيات صفحة السند المتخصص المحددة مباشرة
    const pageId = `voucher-${templateId}`;
    return hasPermission('finance', pageId, action);
  };
  
  /**
   * التحقق من صلاحية الوصول للسندات المتخصصة (وليس قوالب السندات)
   * @param templateId معرف قالب السند المتخصص (اختياري)
   * @param action الإجراء المطلوب التحقق منه
   * @returns {boolean} هل لدى المستخدم صلاحية أم لا
   */
  const hasSpecializedVoucherPermission = (templateId: string | null, action: string): boolean => {
    // إذا كان المستخدم مسؤولاً (admin)، أعط صلاحية كاملة
    if (user?.role === 'admin') {
      return true;
    }

    // التحقق من صلاحية الوصول العامة للسندات المتخصصة أولاً
    const hasGenericVoucherAccess = hasPermission('finance', 'specialized-vouchers', action);
    if (hasGenericVoucherAccess) {
      return true;
    }

    // إذا لم يكن هناك معرف قالب محدد، نكتفي بالتحقق العام
    if (!templateId) {
      return false;
    }

    // التحقق من صلاحيات صفحة القالب المحدد
    const pageId = `voucher-${templateId}`;
    return hasPermission('finance', pageId, action);
  };

  /**
   * التحقق من صلاحية الوصول لإعدادات السندات المتخصصة
   * @param pageId معرف الصفحة الفرعية
   * @param action الإجراء المطلوب التحقق منه
   * @returns {boolean} هل لدى المستخدم صلاحية أم لا
   */
  const hasVoucherSettingPermission = (pageId: string, action: string): boolean => {
    // إذا كان المستخدم مسؤولاً (admin)، أعط صلاحية كاملة
    if (user?.role === 'admin') {
      return true;
    }

    // التحقق من صلاحيات الصفحة المحددة مباشرة
    return hasPermission('finance', pageId, action);
  };

  return {
    hasVoucherTemplatePermission,
    hasVoucherSettingPermission,
    hasSpecializedVoucherPermission
  };
};